/**
 * Post-processing pipeline for markdown-generated HTML.
 * Used by BlogLayout and ComparisonLayout.
 */

export function addHeadingIds(html: string): string {
  return html.replace(
    /<(h[23])>(.*?)<\/\1>/gi,
    (_, tag, text) => {
      const id = text
        .replace(/<[^>]+>/g, '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      return `<${tag} id="${id}">${text}</${tag}>`;
    }
  );
}

export function addCalloutClasses(html: string): string {
  const calloutMap: Record<string, string> = {
    'tl;dr': 'callout--tldr',
    'tldr': 'callout--tldr',
    'summary': 'callout--tldr',
    'quick summary': 'callout--tldr',
    'key takeaways': 'callout--tldr',
    'did you know': 'callout--did-you-know',
    'did you know?': 'callout--did-you-know',
    'stat': 'callout--did-you-know',
    'quick fact': 'callout--quick-fact',
    '📊 quick fact': 'callout--quick-fact',
    'key insight': 'callout--insight',
    'important': 'callout--insight',
    'tip': 'callout--tip',
    'pro tip': 'callout--tip',
    '💡 pro tip': 'callout--tip',
    'if this was useful': 'callout--read-more',
    'read this next': 'callout--read-more',
    'related reading': 'callout--read-more',
    'warning': 'callout--warning',
    'caution': 'callout--warning',
  };

  let result = html.replace(
    /<blockquote>\s*<p>\s*<strong>(.*?)<\/strong>/gi,
    (match, keyword) => {
      const clean = keyword.replace(/[:\s]*$/, '').trim().toLowerCase();
      const cls = calloutMap[clean];
      if (cls) {
        return match.replace('<blockquote>', `<blockquote class="callout ${cls}">`);
      }
      return match;
    }
  );

  // Strip the trailing colon from callout titles (so "Quick Summary:" becomes "Quick Summary")
  // and remove leading 📋/💡/📊 emojis from list items inside callouts (avoids the
  // duplicated emoji-marker look). We do this AFTER classes are added so we can scope it.
  result = result.replace(
    /<blockquote class="callout([^"]*)">([\s\S]*?)<\/blockquote>/g,
    (_, classPart, inner) => {
      // Strip trailing ":" from title (after </strong>)
      const cleaned = inner
        .replace(/(<strong>[^<]+?):(\s*)<\/strong>/, '$1$2</strong>')
        // Strip leading emoji from each <li> (📋 📊 💡 📖 📈 etc.)
        .replace(
          /<li>\s*(?:[\u{1F300}-\u{1FAFF}]|[\u{2600}-\u{27BF}])\s*/gu,
          '<li>'
        );
      return `<blockquote class="callout${classPart}">${cleaned}</blockquote>`;
    }
  );

  return result;
}

export function addStatHighlights(html: string): string {
  let result = html.replace(
    /<strong>([^<]*\d[^<]*)<\/strong>/g,
    '<strong class="stat-highlight">$1</strong>'
  );

  // Inside callouts: also auto-wrap percentages and $amounts that appear in plain
  // text. Bit.ai-style "64%" highlighting — makes stat boxes scannable.
  result = result.replace(
    /<blockquote class="callout([^"]*)">([\s\S]*?)<\/blockquote>/g,
    (_, classPart, inner) => {
      // Skip if this is a structural-only callout (read-more, no inline stats expected)
      if (classPart.includes('callout--read-more')) {
        return `<blockquote class="callout${classPart}">${inner}</blockquote>`;
      }
      // Wrap %, $amounts, and N×/Nx multipliers ONLY when they're not already inside a tag.
      // Pattern matches a number with optional decimals + a % / $ prefix / x suffix /
      // "+" / "K"/"M" suffix, when sitting in text content (between > and <).
      const numberRx = /(>|[\s(])(\$?\d[\d.,]*(?:%|x|×|K|M|B|\+|\s*(?:hours?|hrs?|min(?:utes?)?|seconds?|days?|years?|weeks?|months?)\b)?)/gi;
      const stamped = inner.replace(numberRx, (m: string, before: string, num: string) => {
        // Don't double-wrap if already inside a <strong>
        if (m.includes('<strong')) return m;
        // Skip pure 1-2 digit numbers without a unit (e.g. "step 3" shouldn't highlight)
        if (/^\d{1,2}$/.test(num)) return m;
        return `${before}<strong class="stat-highlight">${num}</strong>`;
      });
      return `<blockquote class="callout${classPart}">${stamped}</blockquote>`;
    }
  );

  return result;
}

export function addFaqCardClasses(html: string): string {
  const faqSplit = html.split(/(<h2[^>]*id="(?:faq|frequently-asked-questions)"[^>]*>)/i);
  if (faqSplit.length < 2) return html;

  const beforeFaq = faqSplit[0];
  const faqSection = faqSplit.slice(1).join('');

  const wrapped = faqSection.replace(
    /<h3([^>]*)>(.*?)<\/h3>/gi,
    '<h3$1 class="faq-card__q">$2</h3>'
  );

  return beforeFaq + '<div class="faq-section">' + wrapped + '</div>';
}

export function processHtml(html: string): string {
  let result = addHeadingIds(html);
  result = addCalloutClasses(result);
  result = addStatHighlights(result);
  result = addFaqCardClasses(result);
  return result;
}
